import React from "react";
import {
  Document,
  Page,
  Text,
  View,
  Image,
  StyleSheet,
} from "@react-pdf/renderer";
import defaultCompanyLogo from "../../../assets/cogent-logo.jpeg";
import {
  PDF_BRAND,
  PDF_BRAND_DARK,
  PDF_BRAND_SURFACE,
  PDF_TABLE_HEADER_BG,
  PDF_TABLE_HEADER_TEXT,
  splitCompanyAddressIntoTwoLines,
  getQuotationSignatory,
  QUOTATION_PDF_PAGE_TOP_PADDING_PT,
  QUOTATION_PDF_PAGE_WITH_TABLEHEAD_PADDING_PT,
  QUOTATION_PDF_FIRST_PAGE_MAX_ROWS,
  QUOTATION_PDF_CONTINUATION_PAGE_MAX_ROWS,
  chunkProductsForQuotationPdf,
  resolveQuotationGstKind,
  buildQuotationGstSummaryRows,
  buildFreightGstSummaryRows,
  appendFreightToGstRateMap,
  formatFreightChargesLineLabel,
  getFreightNotesDisplay,
  parseQuotationMoney,
  formatQuotationMoney,
  pdfCellText,
  pdfHyphenationCallback,
  cleanPaymentTermLine,
  getWarrantyDisplay,
} from "./quotationPdfShared";
import { getOrderDeliveryText } from "./orderDeliveryText";
import { isIndiaCountry } from "../../../constants/countryStates";

// Create StyleSheet for PDF
const styles = StyleSheet.create({
  page: {
    paddingTop: QUOTATION_PDF_PAGE_TOP_PADDING_PT,
    paddingBottom: 35,
    paddingLeft: 40,
    paddingRight: 40,
    fontSize: 10,
    fontFamily: "Helvetica",
    backgroundColor: "#ffffff",
  },
  fixedHeaderBand: {
    position: "absolute",
    top: 35,
    left: 40,
    right: 40,
    backgroundColor: "#ffffff",
  },
  headerSection: {
    marginBottom: 15,
    paddingBottom: 10,
    borderBottomWidth: 2,
    borderBottomColor: PDF_BRAND,
  },
  headerTitle: {
    textAlign: "center",
    fontSize: 16,
    fontWeight: "bold",
    color: PDF_BRAND,
    letterSpacing: 2,
    marginBottom: 10,
  },
  headerTop: {
    display: "flex",
    flexDirection: "row",
    justifyContent: "space-between",
    alignItems: "flex-start",
  },
  headerLogoSide: {
    flexDirection: "row",
    alignItems: "center",
    flex: 1,
  },
  headerSmallLogo: {
    width: 44,
    height: 44,
    objectFit: "contain",
    marginRight: 8,
  },
  companyInfo: {
    flex: 1,
  },
  companyName: {
    fontSize: 13,
    fontWeight: "bold",
    color: PDF_BRAND,
    marginBottom: 2,
  },
  companyNameWithLogo: {
    display: "flex",
    flexDirection: "row",
    alignItems: "center",
    marginBottom: 0,
  },
  companyLogo: {
    width: 42,
    height: 42,
    marginRight: 10,
  },
  headerRight: {
    flex: 1,
    alignItems: "flex-end",
  },
  companyTagline: {
    fontSize: 7,
    color: "#666",
    fontStyle: "italic",
    marginBottom: 2,
  },
  businessInfoText: {
    fontSize: 7,
    color: "#333",
    marginBottom: 1.5,
  },
  businessInfoLabel: {
    fontWeight: "bold",
    color: "#333",
  },
  quotationInfo: {
    textAlign: "right",
  },
  quotationType: {
    fontSize: 9,
    fontWeight: "bold",
    color: "#333",
  },
  revisionText: {
    fontSize: 7,
    color: "#666",
    marginTop: 2,
  },
  quotationNumber: {
    fontSize: 18,
    fontWeight: "bold",
    color: PDF_BRAND,
    marginTop: 3,
  },
  contactGrid: {
    display: "flex",
    flexDirection: "row",
    justifyContent: "space-between",
    gap: 20,
  },
  contactColumn: {
    flex: 1,
  },
  contactLabel: {
    fontSize: 7,
    fontWeight: "bold",
    color: PDF_BRAND,
    textTransform: "uppercase",
    marginBottom: 3,
  },
  contactText: {
    fontSize: 8,
    color: "#333",
    marginBottom: 2,
  },
  detailsGrid: {
    display: "flex",
    flexDirection: "row",
    justifyContent: "space-between",
    gap: 20,
    marginBottom: 15,
    paddingVertical: 8,
  },
  detailsBox: {
    flex: 1,
    paddingHorizontal: 0,
  },
  customerBox: {},

  datesBox: {},
  detailsTitle: {
    fontSize: 9,
    fontWeight: "bold",
    color: PDF_BRAND,
    marginBottom: 5,
  },
  detailsGroup: {
    marginBottom: 2,
    fontSize: 8,
  },
  detailsLabel: {
    fontSize: 8,
    fontWeight: "bold",
    color: "#333",
  },
  detailsValue: {
    fontSize: 8,
    color: "#333",
  },
  customerName: {
    fontSize: 9,
    fontWeight: "bold",
    color: "#000",
    marginBottom: 3,
  },
  tableWrapper: {
    marginBottom: 2,
  },
  tableHeader: {
    display: "flex",
    flexDirection: "row",
    backgroundColor: PDF_TABLE_HEADER_BG,
    borderTopWidth: 1,
    borderTopColor: PDF_BRAND,
    borderBottomWidth: 1,
    borderBottomColor: PDF_BRAND,
  },
  tableRow: {
    display: "flex",
    flexDirection: "row",
    alignItems: "flex-start",
    borderBottomWidth: 1,
    borderBottomColor: PDF_BRAND,
    paddingVertical: 6,
  },
  tableHeaderCell: {
    fontSize: 8,
    fontWeight: "bold",
    color: PDF_TABLE_HEADER_TEXT,
    paddingHorizontal: 4,
    paddingVertical: 6,
    textAlign: "center",
  },
  tableCell: {
    fontSize: 8,
    paddingHorizontal: 3,
    paddingVertical: 4,
    overflow: "hidden",
  },
  colNum: {
    width: "4%",
    maxWidth: "4%",
    textAlign: "center",
    fontWeight: "bold",
    color: "#333",
  },
  colNumHeader: {
    width: "4%",
    maxWidth: "4%",
    textAlign: "center",
    fontWeight: "bold",
    color: PDF_TABLE_HEADER_TEXT,
  },
  colProduct: {
    width: "28%",
    maxWidth: "28%",
    textAlign: "left",
    overflow: "hidden",
  },
  colArticle: {
    width: "14%",
    maxWidth: "14%",
    textAlign: "center",
    paddingHorizontal: 2,
    overflow: "hidden",
  },
  colHsn: {
    width: "10%",
    maxWidth: "10%",
    textAlign: "center",
    paddingHorizontal: 2,
    overflow: "hidden",
  },
  colMoq: {
    width: "6%",
    maxWidth: "6%",
    textAlign: "center",
  },
  colQty: {
    width: "6%",
    maxWidth: "6%",
    textAlign: "center",
  },
  colUnit: {
    width: "7%",
    maxWidth: "7%",
    textAlign: "center",
  },
  colPrice: {
    width: "12%",
    maxWidth: "12%",
    textAlign: "right",
  },
  colGst: {
    width: "10%",
    maxWidth: "10%",
    textAlign: "right",
  },
  colAmount: {
    width: "13%",
    maxWidth: "13%",
    textAlign: "right",
  },
  productName: {
    fontSize: 8,
    fontWeight: "bold",
    color: "#000",
    marginBottom: 2,
    maxWidth: "100%",
  },
  productSpecs: {
    fontSize: 7,
    color: "#000",
    marginTop: 1,
    maxWidth: "100%",
  },
  amountText: {
    color: PDF_BRAND_DARK,
    fontWeight: "bold",
  },
  /** Narrow columns: wrap long article / HSN without overlapping neighbors */
  tableCellMonoWrap: {
    fontSize: 6.5,
    lineHeight: 1.3,
    textAlign: "center",
    paddingHorizontal: 1,
    paddingVertical: 1,
    width: "100%",
    maxWidth: "100%",
  },
  tableCellWrap: {
    fontSize: 8,
    lineHeight: 1.25,
    textAlign: "left",
    width: "100%",
    maxWidth: "100%",
  },
  summarySection: {
    display: "flex",
    flexDirection: "row",
    justifyContent: "space-between",
    gap: 25,
    marginTop: 10,
    marginBottom: 15,
  },
  termsBox: {
    flex: 1,
    padding: 8,
    borderWidth: 1,
    borderColor: PDF_BRAND,
    borderRadius: 3,
    backgroundColor: PDF_BRAND_SURFACE,
  },
  summaryBox: {
    flex: 1,
    padding: 8,
    borderWidth: 1,
    borderColor: PDF_BRAND,
    borderRadius: 3,
    backgroundColor: PDF_BRAND_SURFACE,
  },
  termsTitle: {
    fontSize: 8,
    fontWeight: "bold",
    color: "#000",
    marginBottom: 4,
  },
  termsList: {
    fontSize: 7,
    color: "#333",
    lineHeight: 1.3,
  },
  summaryRow: {
    display: "flex",
    flexDirection: "row",
    justifyContent: "space-between",
    marginBottom: 3,
    paddingBottom: 3,
    borderBottomWidth: 1,
    borderBottomColor: PDF_BRAND,
  },
  summaryLabel: {
    fontSize: 8,
    color: "#333",
  },
  summaryValue: {
    fontSize: 8,
    color: "#333",
    fontWeight: "normal",
  },
  summaryValueNegative: {
    fontSize: 8,
    color: "#d32f2f",
    fontWeight: "normal",
  },
  grandTotalRow: {
    display: "flex",
    flexDirection: "row",
    justifyContent: "space-between",
    marginTop: 5,
    paddingTop: 3,
  },
  grandTotalLabel: {
    fontSize: 9,
    fontWeight: "bold",
    color: PDF_BRAND_DARK,
  },
  grandTotalValue: {
    fontSize: 9,
    fontWeight: "bold",
    color: PDF_BRAND_DARK,
  },
  footerSection: {
    borderTopWidth: 2,
    borderTopColor: PDF_BRAND,
    paddingTop: 15,
  },
  footerContent: {
    display: "flex",
    flexDirection: "row",
    justifyContent: "space-between",
    position: "relative",
  },
  footerLeft: {
    flex: 1,
  },
  footerRight: {
    position: "absolute",
    right: 0,
    top: 0,
    textAlign: "right",
  },
  footerText: {
    fontSize: 8,
    color: "#333",
    marginBottom: 3,
  },
  footerLabel: {
    fontWeight: "bold",
  },
  stampImage: {
    width: 80,
    height: 80,
    marginBottom: 8,
  },
  signature: {
    marginTop: 4,
    borderTopWidth: 1,
    borderTopColor: "#999",
    paddingTop: 2,
  },
  signatureText: {
    fontSize: 7,
    color: "#666",
  },
  finalNote: {
    marginTop: 20,
    textAlign: "center",
    fontSize: 7,
    color: "#999",
    fontStyle: "italic",
    borderTopWidth: 1,
    borderTopColor: "#ddd",
    paddingTop: 8,
  },
  documentEndFooter: {
    marginTop: 18,
    borderTopWidth: 1,
    borderTopColor: PDF_BRAND,
    paddingTop: 8,
    alignItems: "center",
  },
  letterSection: {
    marginBottom: 12,
  },
  letterLine: {
    fontSize: 8.5,
    color: PDF_BRAND_DARK,
    fontWeight: "bold",
    marginBottom: 5,
  },
  letterBody: {
    fontSize: 8.5,
    color: "#333",
    fontWeight: "bold",
    marginBottom: 5,
    lineHeight: 1.5,
  },
});

export const QuotationTemplate = ({ order, settings }: any) => {
  const companyName = settings?.companyName || "Cogent Tech Solutions";
  const companyNameUpper = (companyName || "").trim().toUpperCase() || "COGENT TECH SOLUTIONS";
  const { name: signatoryName, mobile: signatoryMobile } = getQuotationSignatory(order);
  const companyAddress = settings?.companyAddress || "";
  const companyPhone = settings?.companyPhone || "";
  const companyEmail = settings?.companyEmail || "";
  const companyGst = settings?.companyGst || "";
  const companyCountry = String(settings?.companyCountry ?? "India").trim() || "India";
  const companyState = String(settings?.companyState ?? "").trim();
  const companyAddressLines = splitCompanyAddressIntoTwoLines(companyAddress);
  const logoSrc =
    typeof settings?.companyLogo === "string" && settings.companyLogo.trim().length > 0
      ? settings.companyLogo.trim()
      : defaultCompanyLogo;
  const bankAccountHolder = settings?.bankAccountHolder || "Cogent";
  const bankName = settings?.bankName || "";
  const bankAccountNo = settings?.bankAccountNo || "";
  const bankBranch = settings?.bankBranch || "";
  const bankIfsc = settings?.bankIfsc || "";
  // ── Number-to-words (Indian numbering system) ──────────────────────────
  const numberToWords = (n: number): string => {
    const ones = ["","One","Two","Three","Four","Five","Six","Seven","Eight","Nine","Ten","Eleven","Twelve","Thirteen","Fourteen","Fifteen","Sixteen","Seventeen","Eighteen","Nineteen"];
    const tens = ["","","Twenty","Thirty","Forty","Fifty","Sixty","Seventy","Eighty","Ninety"];
    const toWords = (num: number): string => {
      if (num === 0) return "";
      if (num < 20) return ones[num] + " ";
      if (num < 100) return tens[Math.floor(num / 10)] + " " + toWords(num % 10);
      if (num < 1000) return ones[Math.floor(num / 100)] + " Hundred " + toWords(num % 100);
      if (num < 100000) return toWords(Math.floor(num / 1000)) + "Thousand " + toWords(num % 1000);
      if (num < 10000000) return toWords(Math.floor(num / 100000)) + "Lakh " + toWords(num % 100000);
      return toWords(Math.floor(num / 10000000)) + "Crore " + toWords(num % 10000000);
    };
    const intPart = Math.floor(n);
    const decPart = Math.round((n - intPart) * 100);
    let result = toWords(intPart).trim() + " Rupees";
    if (decPart > 0) result += " and " + toWords(decPart).trim() + " Paise";
    return result + " only";
  };

  const transport = parseFloat(order?.freightCharges || order?.transport) || 0;
  const fixing = parseFloat(order?.fixing) || 0;
  const discount = parseFloat(order?.discount) || 0;

  // ── GST: intrastate (CGST+SGST), interstate India (IGST full rate), or foreign (single GST line) ──
  const allProducts: any[] = order?.products || [];
  const productChunks = chunkProductsForQuotationPdf(
    allProducts,
    QUOTATION_PDF_FIRST_PAGE_MAX_ROWS,
    QUOTATION_PDF_CONTINUATION_PAGE_MAX_ROWS,
  );
  // subTotal = sum of (price × qty) WITHOUT any GST — use order.price (pre-GST)
  const subTotal = parseFloat(order?.price) || 0;

  // Build a map: gstRate → taxable base (price × qty)
  const gstRateMap: Record<number, number> = {};
  allProducts.forEach((p: any) => {
    const rate = parseFloat(p.gst?.toString() || "0");
    const base = parseFloat(p.price?.toString() || "0") * parseFloat(p.nos?.toString() || "1");
    if (rate > 0) {
      gstRateMap[rate] = (gstRateMap[rate] || 0) + base;
    }
  });
  const gstKind = resolveQuotationGstKind(
    String(order?.country_ ?? "India"),
    companyCountry,
    String(order?.state_ ?? ""),
    companyState,
  );
  const productGstRows = buildQuotationGstSummaryRows(gstRateMap, gstKind);
  const freightGstPct =
    parseFloat(
      String(
        (order as any)?.freightChargesGst ??
          (order as any)?.freight_charges_gst_ ??
          "",
      ).replace(/,/g, ""),
    ) || 18;
  // Fold freight into CGST/SGST/IGST so freight 18% tax appears in those lines.
  const gstRows = buildQuotationGstSummaryRows(
    appendFreightToGstRateMap(gstRateMap, transport, freightGstPct),
    gstKind,
  );
  const freightGstRows = buildFreightGstSummaryRows(transport, freightGstPct, gstKind);
  const freightTaxOnFreightOnly = freightGstRows.reduce((s, r) => s + r.amount, 0);
  const totalProductGst = productGstRows.reduce((s, r) => s + r.amount, 0);
  const totalGst = totalProductGst + freightTaxOnFreightOnly;
  const grandTotal = subTotal + transport + fixing + totalGst - discount;
  const roundOff = Math.round(grandTotal) - grandTotal;
  const grandTotalRounded = Math.round(grandTotal);

  const formatDate = (date: any) => {
    if (!date) return "N/A";
    const d = new Date(date);
    const day = String(d.getDate()).padStart(2, "0");
    const month = String(d.getMonth() + 1).padStart(2, "0");
    const year = d.getFullYear();
    return `${day}-${month}-${year}`;
  };

  const formatIndiaTime = (dateString: string | Date | undefined) => {
    if (!dateString || dateString === "") return "N/A";
    const date = new Date(dateString);
    if (isNaN(date.getTime())) return "N/A";
    return date
      .toLocaleDateString("en-IN", {
        day: "2-digit",
        month: "2-digit",
        year: "numeric",
        hour: "2-digit",
        minute: "2-digit",
        hour12: false,
        timeZone: "Asia/Kolkata",
      })
      .replace(",", "");
  };

  const formatDateOnly = (
    dateString: string | Date | undefined
  ) => {
    if (!dateString || dateString === "") return "N/A";
    const date = new Date(dateString);
    if (isNaN(date.getTime())) return "N/A";
    return date
      .toLocaleDateString("en-IN", {
        day: "2-digit",
        month: "2-digit",
        year: "numeric",
        timeZone: "Asia/Kolkata",
      })
      .replace(/\//g, "-");
  };

  // Header Component - Repeats on every page
  const Header = () => (
    <View style={styles.headerSection}>
      {/* Centered QUOTATION title */}
      <Text style={styles.headerTitle}>QUOTATION</Text>

      {/* Row: left = logo, right = company details */}
      <View style={styles.headerTop}>
        <View style={styles.headerLogoSide}>
          <Image src={logoSrc} style={styles.companyLogo} />
        </View>
        <View style={styles.headerRight}>
          <Text style={styles.companyName}>{companyName}</Text>
          {companyAddress ? (
            <>
              <Text style={styles.businessInfoText}>{companyAddressLines.line1}</Text>
              {companyAddressLines.line2 ? (
                <Text style={styles.businessInfoText}>{companyAddressLines.line2}</Text>
              ) : null}
            </>
          ) : null}
          {(companyPhone || companyEmail) ? <Text style={styles.businessInfoText}>{[companyPhone, companyEmail].filter(Boolean).join(" | ")}</Text> : null}
          {companyGst && isIndiaCountry(companyCountry) ? (
          <Text style={styles.businessInfoText}>
            <Text style={styles.businessInfoLabel}>GSTIN: </Text>{companyGst}
            {companyState ? (
              <>
                <Text> | </Text>
                <Text style={styles.businessInfoLabel}>State: </Text>{companyState}
              </>
            ) : null}
          </Text>
          ) : companyGst ? (
            <Text style={styles.businessInfoText}>
              <Text style={styles.businessInfoLabel}>Tax ID: </Text>{companyGst}
            </Text>
          ) : null}
        </View>
      </View>
    </View>
  );

  // Details Section
  const DetailsSection = () => (
    <View style={styles.detailsGrid}>
      {/* Left: Quotation For */}
      <View style={{ ...styles.detailsBox, ...styles.customerBox }}>
        <Text style={styles.detailsTitle}>Quotation For</Text>
        {order.company_name_ && (
          <Text style={styles.detailsGroup}>
            {/* <Text style={styles.detailsLabel}>Company Name: </Text> */}
            {order.company_name_}
          </Text>
        )}
        {order.address_ && (
          <Text style={styles.detailsGroup}>
            {/* <Text style={styles.detailsLabel}>Address: </Text> */}
            {order.address_}
          </Text>
        )}
       {/*{order.person_name_ && (
          //<Text style={styles.detailsGroup}>
            //{/* <Text style={styles.detailsLabel}>Person Name: </Text> */}
        {/* //     {order.person_name_}
        //   </Text>
        // )}
        // {order.person_mobile_ && ( */}
        {/* //   <Text style={styles.detailsGroup}>
        //     {/* <Text style={styles.detailsLabel}>Person Mobile: </Text> */}
        {/* //     {order.person_mobile_}
        //   </Text>
        // )}  */}
        {order.country_ ? (
          <Text style={styles.detailsGroup}>
            <Text style={styles.detailsLabel}>Country: </Text>
            {order.country_}
          </Text>
        ) : null}
        {order.state_ ? (
          <Text style={styles.detailsGroup}>
            <Text style={styles.detailsLabel}>State / UT: </Text>
            {order.state_}
          </Text>
        ) : null}
        {isIndiaCountry(order.country_ || "India") && (order.gst_ || order.customerGst_) ? (
          <Text style={styles.detailsGroup}>
            <Text style={styles.detailsLabel}>GSTIN: </Text>
            {order.gst_ || order.customerGst_}
          </Text>
        ) : null}
      </View>

      {/* Right: Quotation Details (right-aligned, under company block) */}
      <View style={[styles.detailsBox, { alignItems: "flex-end" }]}>
        <Text style={[styles.detailsTitle, { textAlign: "right", width: "100%" }]}>Quotation Details</Text>
        <Text style={[styles.detailsGroup, { textAlign: "right", width: "100%" }]}>
          <Text style={styles.detailsLabel}>Quotation No: </Text>
          {order.quotationNumber || order.id}
        </Text>
        <Text style={[styles.detailsGroup, { textAlign: "right", width: "100%" }]}>
          <Text style={styles.detailsLabel}>Date: </Text>
          {formatDate(order.quotationDate || order.createdAt)}
        </Text>
      </View>
    </View>
  );

  // Letter Preamble Section
  const LetterSection = () => (
    <View style={styles.letterSection}>
      <Text style={styles.letterLine}>
        <Text style={{ color: PDF_BRAND_DARK }}>Kind Attn : </Text>
        <Text style={{ color: "#000000" }}>
          {String(order.kindAttention || "").trim() ||
            `${order.person_name_ || ""}${order.person_name_ && order.person_mobile_ ? " / " : ""}${order.person_mobile_ || ""}`}
        </Text>
      </Text>
      <Text style={styles.letterLine}>Dear Sir / Madam</Text>
      {order.subject ? (
        <Text style={styles.letterLine}>Sub :    {order.subject}</Text>
      ) : null}
      <Text style={styles.letterLine}>Greetings from {companyNameUpper},</Text>
      <Text style={styles.letterBody}>
        Further to the above references, we are pleased to submit our offer for the same, kindly see below Price Schedule along with Commercial Terms &amp; Conditions.
      </Text>
    </View>
  );

  // Products Table Header
  const TableHeader = () => (
    <View style={styles.tableHeader}>
      <Text style={{ ...styles.tableHeaderCell, ...styles.colNumHeader }}>S.No</Text>
      <Text style={{ ...styles.tableHeaderCell, ...styles.colProduct }}>Description</Text>
      <Text style={{ ...styles.tableHeaderCell, ...styles.colArticle }}>Article No</Text>
      <Text style={{ ...styles.tableHeaderCell, ...styles.colHsn }}>HSN</Text>
      <Text style={{ ...styles.tableHeaderCell, ...styles.colMoq }}>MOQ</Text>
      <Text style={{ ...styles.tableHeaderCell, ...styles.colQty }}>Qty</Text>
      <Text style={{ ...styles.tableHeaderCell, ...styles.colUnit }}>Unit</Text>
      <Text style={{ ...styles.tableHeaderCell, ...styles.colPrice }}>Price</Text>
      <Text style={{ ...styles.tableHeaderCell, ...styles.colAmount }}>Amount</Text>
    </View>
  );

  const FixedQuotationTop = ({ withTableHeader }: { withTableHeader: boolean }) => (
    <View style={styles.fixedHeaderBand} fixed>
      <Header />
      {withTableHeader ? <TableHeader /> : null}
    </View>
  );

  // Summary (bordered) + sign-off outside the bordered totals box (single View so react-pdf keeps layout + sign-off together)
  const SummarySection = () => (
    <View wrap={false}>
    <View style={{ marginTop: 8, borderWidth: 1, borderColor: PDF_BRAND }}>
      {/* Row 1: Two columns — Amount+Bank | Tax Breakdown */}
      <View style={{ flexDirection: "row", borderBottomWidth: 1, borderBottomColor: PDF_BRAND }}>
        {/* LEFT: Amount In Words + Bank Details */}
        <View style={{ flex: 1, padding: 8, borderRightWidth: 1, borderRightColor: PDF_BRAND }}>
          <Text style={{ fontSize: 8, fontWeight: "bold", color: PDF_BRAND_DARK, marginBottom: 3 }}>Quotation Amount In Words</Text>
          <Text style={{ fontSize: 7.5, color: "#333", marginBottom: 8, lineHeight: 1.4 }}>{numberToWords(grandTotalRounded)}</Text>
          <Text style={{ fontSize: 8, fontWeight: "bold", color: PDF_BRAND_DARK, marginBottom: 4 }}>Company Bank Details</Text>
          {bankAccountHolder ? <Text style={{ fontSize: 7, color: "#333", lineHeight: 1.5 }}>Account Holder Name : {bankAccountHolder}</Text> : null}
          {bankName ? <Text style={{ fontSize: 7, color: "#333", lineHeight: 1.5 }}>Bank Name : {bankName}</Text> : null}
          {bankAccountNo ? <Text style={{ fontSize: 7, color: "#333", lineHeight: 1.5 }}>A/c No : {bankAccountNo}</Text> : null}
          {bankBranch ? <Text style={{ fontSize: 7, color: "#333", lineHeight: 1.5 }}>Branch : {bankBranch}</Text> : null}
          {bankIfsc ? <Text style={{ fontSize: 7, color: "#333", lineHeight: 1.5 }}>IFSC Code : {bankIfsc}</Text> : null}
        </View>

        {/* RIGHT: Tax Breakdown table */}
        <View style={{ width: "42%" }}>
          <View style={{ flexDirection: "row", justifyContent: "space-between", paddingHorizontal: 8, paddingVertical: 5, borderBottomWidth: 1, borderBottomColor: PDF_BRAND }}>
            <Text style={styles.summaryLabel}>Sub Total</Text>
            <Text style={styles.summaryValue}>{subTotal.toFixed(2)}</Text>
          </View>

          {discount > 0 && (
            <View style={{ flexDirection: "row", justifyContent: "space-between", paddingHorizontal: 8, paddingVertical: 5, borderBottomWidth: 1, borderBottomColor: PDF_BRAND }}>
              <Text style={styles.summaryLabel}>Discount</Text>
              <Text style={styles.summaryValueNegative}>-{discount.toFixed(2)}</Text>
            </View>
          )}

          {fixing > 0 && (
            <View style={{ flexDirection: "row", justifyContent: "space-between", paddingHorizontal: 8, paddingVertical: 5, borderBottomWidth: 1, borderBottomColor: PDF_BRAND }}>
              <Text style={styles.summaryLabel}>Fixing</Text>
              <Text style={styles.summaryValue}>{fixing.toFixed(2)}</Text>
            </View>
          )}

          {/* GST includes product tax + freight 18% (CGST/SGST or IGST) */}
          {gstRows.map((row) => (
            <View key={row.key} style={{ flexDirection: "row", justifyContent: "space-between", paddingHorizontal: 8, paddingVertical: 5, borderBottomWidth: 1, borderBottomColor: PDF_BRAND }}>
              <Text style={styles.summaryLabel}>{row.label}</Text>
              <Text style={styles.summaryValue}>{row.amount.toFixed(2)}</Text>
            </View>
          ))}

          {transport > 0 && (
            <View style={{ flexDirection: "row", justifyContent: "space-between", paddingHorizontal: 8, paddingVertical: 5, borderBottomWidth: 1, borderBottomColor: PDF_BRAND }}>
              <Text style={styles.summaryLabel}>
                {formatFreightChargesLineLabel(transport, freightGstPct)}
              </Text>
              <Text style={styles.summaryValue}>
                {transport.toFixed(2)}
              </Text>
            </View>
          )}

          {Math.abs(roundOff) >= 0.01 && (
            <View style={{ flexDirection: "row", justifyContent: "space-between", paddingHorizontal: 8, paddingVertical: 5, borderBottomWidth: 1, borderBottomColor: PDF_BRAND }}>
              <Text style={styles.summaryLabel}>Round Off</Text>
              <Text style={styles.summaryValue}>{roundOff >= 0 ? "+" : ""}{roundOff.toFixed(2)}</Text>
            </View>
          )}
        </View>
      </View>

      {/* Grand Total — full width bar (inside bordered summary only) */}
      <View style={{ flexDirection: "row", justifyContent: "space-between", backgroundColor: PDF_BRAND, paddingHorizontal: 10, paddingVertical: 7 }}>
        <Text style={{ fontSize: 11, fontWeight: "bold", color: "#ffffff" }}>Grand Total</Text>
        <Text style={{ fontSize: 11, fontWeight: "bold", color: "#ffffff" }}>{grandTotalRounded.toFixed(2)}</Text>
      </View>
    </View>

    {/* Sign-off outside the bordered grand-total / summary table */}
    <View style={{ marginTop: 10, paddingHorizontal: 2, paddingVertical: 4 }}>
      <Text style={{ fontSize: 8, color: "#333", marginBottom: 3 }}>Yours Faithfully,</Text>
      <Text style={{ fontSize: 8, fontWeight: "bold", color: "#000", marginBottom: 8 }}>For {companyNameUpper}</Text>
      {signatoryName ? (
        <Text style={{ fontSize: 8, color: "#000", textAlign: "left" }}>{signatoryName}</Text>
      ) : null}
      {signatoryMobile ? (
        <Text style={{ fontSize: 8, color: "#000", textAlign: "left" }}>Mobile - {signatoryMobile}</Text>
      ) : null}
    </View>
    </View>
  );

  const renderProductRowsFrom = (chunk: any[], serialStart: number) =>
    chunk.map((p: any, index: number) => {
      const displayNo = serialStart + index + 1;
      const unitPrice = parseQuotationMoney(p.price);
      const qty = Math.max(1, parseQuotationMoney(p.nos) || 1);
      const amount = unitPrice * qty;
      const title = p.name || "N/A";
      const spec = p.description ? p.description : "";
      return (
        <View key={`row-${serialStart}-${index}`} wrap={false} style={styles.tableRow}>
          <Text style={{ ...styles.tableCell, ...styles.colNum }}>
            {displayNo}
          </Text>
          <View style={{ ...styles.tableCell, ...styles.colProduct }}>
            <Text
              style={{ ...styles.productName, ...styles.tableCellWrap }}
              wrap
              hyphenationCallback={pdfHyphenationCallback(12)}
            >
              {pdfCellText(title)}
            </Text>
            {spec ? (
              <Text
                style={{ ...styles.productSpecs, ...styles.tableCellWrap }}
                wrap
                hyphenationCallback={pdfHyphenationCallback(12)}
              >
                {pdfCellText(spec)}
              </Text>
            ) : null}
          </View>
          <View style={{ ...styles.tableCell, ...styles.colArticle }}>
            <Text
              style={styles.tableCellMonoWrap}
              wrap
              hyphenationCallback={pdfHyphenationCallback(8)}
            >
              {pdfCellText(p.article_no)}
            </Text>
          </View>
          <View style={{ ...styles.tableCell, ...styles.colHsn }}>
            <Text
              style={styles.tableCellMonoWrap}
              wrap
              hyphenationCallback={pdfHyphenationCallback(8)}
            >
              {pdfCellText(p.hsn_code)}
            </Text>
          </View>
          <Text style={{ ...styles.tableCell, ...styles.colMoq }}>
            {p.moq || "-"}
          </Text>
          <Text style={{ ...styles.tableCell, ...styles.colQty }}>
            {p.nos || "1"}
          </Text>
          <Text style={{ ...styles.tableCell, ...styles.colUnit }}>
            {p.unit || p.area_uom || "-"}
          </Text>
          <Text style={{ ...styles.tableCell, ...styles.colPrice }}>
            {formatQuotationMoney(unitPrice)}
          </Text>
          <Text style={{ ...styles.tableCell, ...styles.colAmount, ...styles.amountText }}>
            {formatQuotationMoney(amount)}
          </Text>
        </View>
      );
    });

  return (
    <Document title={`Quotation_${order.id}`}>
      {productChunks.map((chunk, chunkIndex) => {
        const isFirst = chunkIndex === 0;
        const isLast = chunkIndex === productChunks.length - 1;
        let serialStart = 0;
        for (let j = 0; j < chunkIndex; j++) serialStart += productChunks[j].length;
        return (
          <Page
            key={`quotation-products-${chunkIndex}`}
            size="A4"
            wrap={false}
            style={[
              styles.page,
              {
                paddingTop: isFirst
                  ? QUOTATION_PDF_PAGE_TOP_PADDING_PT
                  : QUOTATION_PDF_PAGE_WITH_TABLEHEAD_PADDING_PT,
              },
            ]}
          >
            <FixedQuotationTop withTableHeader={!isFirst} />
            {isFirst ? (
              <>
                <DetailsSection />
                <LetterSection />
                <View style={styles.tableWrapper}>
                  <TableHeader />
                  {renderProductRowsFrom(chunk, serialStart)}
                </View>
              </>
            ) : (
              <View style={styles.tableWrapper}>{renderProductRowsFrom(chunk, serialStart)}</View>
            )}
            {isLast ? (
              <>
                <SummarySection />
              </>
            ) : null}
          </Page>
        );
      })}

      {/* ── Terms & Conditions — always the last page ── */}
      <Page size="A4" style={styles.page} wrap>
        <FixedQuotationTop withTableHeader={false} />

        {/* T&C Title + Quotation reference */}
        <View style={{ alignItems: "center", marginBottom: 8, marginTop: 4 }}>
          <Text style={{ fontSize: 13, fontWeight: "bold", color: PDF_BRAND_DARK, letterSpacing: 1 }}>Terms and Conditions</Text>
        </View>
        <View style={{ flexDirection: "row", justifyContent: "space-between", marginBottom: 12, borderBottomWidth: 1, borderBottomColor: PDF_BRAND, paddingBottom: 6 }}>
          <Text style={{ fontSize: 8 }}>Q.No : {order.quotationNumber || order.invoiceId || order.id}</Text>
          <Text style={{ fontSize: 8 }}>Date : {formatDateOnly(order.quotationDate || order.createdAt)}</Text>
        </View>

        {/* Clauses */}
        <View style={{ marginBottom: 7 }}>
          <Text style={{ fontSize: 8, fontWeight: "bold", color: PDF_BRAND_DARK }}>1. Price Basis</Text>
          <Text style={{ fontSize: 7.5, marginTop: 2, color: "#333" }}>The Prices quoted are Ex-works Chennai. GST extra as applicable.</Text>
        </View>
        <View style={{ marginBottom: 7 }}>
          <Text style={{ fontSize: 8, fontWeight: "bold", color: PDF_BRAND_DARK }}>2. Packing and Forwarding</Text>
          <Text style={{ fontSize: 7.5, marginTop: 2, color: "#333" }}>Packing shall be of standard packing at No cost.</Text>
        </View>
        <View style={{ marginBottom: 7 }}>
          <Text style={{ fontSize: 8, fontWeight: "bold", color: PDF_BRAND_DARK }}>3. Freight Charges</Text>
          <Text style={{ fontSize: 7.5, marginTop: 2, color: "#333" }}>
            {getFreightNotesDisplay(order)}
          </Text>
        </View>
        <View style={{ marginBottom: 7 }}>
          <Text style={{ fontSize: 8, fontWeight: "bold", color: PDF_BRAND_DARK }}>4. Insurance</Text>
          <Text style={{ fontSize: 7.5, marginTop: 2, color: "#333" }}>In your Scope.</Text>
        </View>
        <View style={{ marginBottom: 7 }}>
          <Text style={{ fontSize: 8, fontWeight: "bold", color: PDF_BRAND_DARK }}>5. Delivery</Text>
          <Text style={{ fontSize: 7.5, marginTop: 2, color: "#333" }}>{getOrderDeliveryText(order) || "As mutually agreed."}</Text>
        </View>
        <View style={{ marginBottom: 7 }}>
          <Text style={{ fontSize: 8, fontWeight: "bold", color: PDF_BRAND_DARK }}>6. Validity</Text>
          <Text style={{ fontSize: 7.5, marginTop: 2, color: "#333" }}>{order.validity ? `This quotation is valid for ${order.validity}.` : "As mutually agreed."}</Text>
        </View>
        <View style={{ marginBottom: 7 }}>
          <Text style={{ fontSize: 8, fontWeight: "bold", color: PDF_BRAND_DARK }}>7. Payment Terms</Text>
          {order.terms
            ? order.terms.split("\n").filter((l: string) => l.trim()).map((l: string, i: number) => {
                const cl = cleanPaymentTermLine(l);
                return cl ? <Text key={i} style={{ fontSize: 7.5, color: "#333", marginTop: 2 }}>{cl}</Text> : null;
              })
            : <Text style={{ fontSize: 7.5, color: "#333", marginTop: 2 }}>50% advance, balance on completion.</Text>
          }
        </View>
        <View style={{ marginBottom: 7 }}>
          <Text style={{ fontSize: 8, fontWeight: "bold", color: PDF_BRAND_DARK }}>8. Warranty</Text>
          <Text style={{ fontSize: 7.5, marginTop: 2, color: "#333" }}>{getWarrantyDisplay(order)}</Text>
        </View>
        <View style={{ marginBottom: 7 }}>
          <Text style={{ fontSize: 8, fontWeight: "bold", color: PDF_BRAND_DARK }}>9. Statutory Variations</Text>
          <Text style={{ fontSize: 7.5, marginTop: 2, color: "#333" }}>Any impact on the price due to variation in taxes and duties applicable to Vendor scope of supply shall be to Purchaser account at actual against documentary evidence. Statutory variations shall include any variation in existing statutes or due to a new statute.</Text>
        </View>
        <View style={{ marginBottom: 7 }}>
          <Text style={{ fontSize: 8, fontWeight: "bold", color: PDF_BRAND_DARK }}>10. Force Majeure</Text>
          <Text style={{ fontSize: 7.5, marginTop: 2, color: "#333" }}>Neither the Vendor nor the Purchaser shall be liable for delays caused by force majeure conditions including Acts of God, Fires, Epidemics, Floods, Riots, Wars, Sabotage, Strikes, or Lock-outs.</Text>
        </View>
        <View style={{ marginBottom: 7 }}>
          <Text style={{ fontSize: 8, fontWeight: "bold", color: PDF_BRAND_DARK }}>11. Order Placement</Text>
          <Text style={{ fontSize: 7.5, marginTop: 2, color: "#333", marginLeft: 8 }}>M/s Cogent, No - 1/7, Sethu Rama Iyer Street, Ramapuram, Chennai - 600089</Text>
        </View>
        <View style={{ marginBottom: 7 }}>
          <Text style={{ fontSize: 8, fontWeight: "bold", color: PDF_BRAND_DARK }}>12. Order Cancellation</Text>
          <Text style={{ fontSize: 7.5, marginTop: 2, color: "#333" }}>Cancellation of the purchase order cannot be accepted and advance payment are non refundable.</Text>
        </View>

        <View style={styles.documentEndFooter}>
          <Text style={{ fontSize: 7, color: "#555", fontStyle: "italic" }}>Automation towards Innovation</Text>
        </View>

      </Page>
    </Document>
  );
};